import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import type {RepoStackSummary} from '@github-ui/dotcom-schema/types/pull_requests/v1/repo_stacks'
import {Link} from '@github-ui/react-router'
import {StackIcon} from '@primer/octicons-react'
import {BranchName, RelativeTime, VisuallyHidden} from '@primer/react'
import {memo} from 'react'

import {StackClosedPullWarning} from './StackClosedPullWarning'
import {StackProgressMeter} from './StackProgressMeter'
import {mergedSegments} from './stack-segments'
import styles from './Stacks.module.css'

export const StackListRow = memo(function StackListRowComponent({stack}: {stack: RepoStackSummary}) {
  const merged = Math.min(stack.mergedCount, stack.size)
  const pullRequests = stack.size === 1 ? '1 pull request' : `${stack.size} pull requests`
  const iconColor = stack.open
    ? 'var(--fgColor-open)'
    : merged < stack.size
      ? 'var(--fgColor-closed)'
      : 'var(--fgColor-done)'

  return (
    <ListItem
      role="listitem"
      className={styles.row}
      title={<ListItemTitle value={`Stack #${stack.number}`} href={stack.url} linkProps={{as: Link, to: stack.url}} />}
      metadata={
        <ListItemMetadata alignment="right" className={styles.RowProgress}>
          <StackProgressMeter
            className={styles.ListMeter}
            segments={mergedSegments(merged, stack.size, stack.open ? 'unmerged' : 'closed')}
          />
          <span aria-hidden="true" className={styles.ProgressLabel}>
            {merged} / {stack.size} merged
          </span>
          <VisuallyHidden>
            {merged} of {pullRequests} merged
          </VisuallyHidden>
        </ListItemMetadata>
      }
    >
      <ListItemLeadingContent>
        <ListItemLeadingVisual
          icon={StackIcon}
          color={iconColor}
          description={stack.open ? 'Open stack' : 'Completed stack'}
        />
      </ListItemLeadingContent>
      <ListItemMainContent>
        <ListItemDescription>
          <span>
            {pullRequests} targeting{' '}
            <BranchName as="span" className={styles.BranchName}>
              {stack.baseRefName}
            </BranchName>
          </span>{' '}
          <span className={styles.MetadataGroup}>
            <span aria-hidden="true">&middot;</span>{' '}
            <span>
              created <RelativeTime date={new Date(stack.createdAt)} noTitle />
            </span>
          </span>
          {stack.closedPullRequest && (
            <>
              {' '}
              <span className={styles.MetadataGroup}>
                <span aria-hidden="true">&middot;</span>{' '}
                <StackClosedPullWarning closedPullRequest={stack.closedPullRequest} />
              </span>
            </>
          )}
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
})
